import React from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useBreakpoints } from 'lib.hooks/src/browser';
import './styles.scss';

const { RangePicker } = DatePicker;

interface DateRangeComponentProps {
  isDrawer?: boolean;
  filters: {
    dateFrom?: string;
    dateTo?: string;
  };
  finishDatePlaceholder?: string;
  onDateValueChange: (dates: { dateFrom?: string; dateTo?: string }) => void;
  disabledDate?: (current: dayjs.Dayjs) => boolean;
  disabledStartDate?: (current: dayjs.Dayjs) => boolean;
  disabledEndDate?: (current: dayjs.Dayjs) => boolean;
  allowClear?: boolean;
  dataTestId?: string;
}

const DateRangeComponent: React.FC<DateRangeComponentProps> = ({
  isDrawer,
  filters,
  onDateValueChange,
  finishDatePlaceholder,
  disabledDate,
  disabledStartDate: disabledStartDateProp,
  disabledEndDate: disabledEndDateProp,
  allowClear = true,
  dataTestId,
}) => {
  const isDesktop = useBreakpoints().md;

  const internalDisabledStartDate = (current: dayjs.Dayjs) => {
    return !!filters?.dateTo && current > dayjs(filters.dateTo);
  };

  const internalDisabledEndDate = (current: dayjs.Dayjs) => {
    return !!filters?.dateFrom && current < dayjs(filters.dateFrom);
  };

  const startDateDisabled = (current: dayjs.Dayjs): boolean =>
    (disabledStartDateProp ?? internalDisabledStartDate)(current) ||
    (disabledDate?.(current) ?? false);
  const endDateDisabled = (current: dayjs.Dayjs): boolean =>
    (disabledEndDateProp ?? internalDisabledEndDate)(current) || (disabledDate?.(current) ?? false);
  const rangeDisabled = (current: dayjs.Dayjs): boolean =>
    startDateDisabled(current) || endDateDisabled(current);

  return isDesktop ? (
    <div data-test-id={dataTestId} className={isDrawer ? 'w-full' : ''}>
      <RangePicker
        style={isDrawer ? { width: '100%' } : {}}
        format="YYYY-MM-DD"
        inputReadOnly={false}
        value={[
          filters?.dateFrom ? dayjs(filters.dateFrom) : null,
          filters?.dateTo ? dayjs(filters.dateTo) : null,
        ]}
        onChange={(_, dateStrings) => {
          const [dateFrom, dateTo] = dateStrings as string[];
          onDateValueChange({ dateFrom, dateTo });
        }}
        disabledDate={rangeDisabled}
        allowClear={allowClear}
      />
    </div>
  ) : (
    <div className="flex w-full mb-12 gap-4" data-test-id={dataTestId}>
      <DatePicker
        style={{ width: '100%' }}
        format="YYYY-MM-DD"
        value={filters?.dateFrom ? dayjs(filters.dateFrom) : null}
        onChange={(_, from) => onDateValueChange({ dateFrom: from as string })}
        placeholder="Start Date"
        allowClear={allowClear}
        disabledDate={startDateDisabled}
      />
      <DatePicker
        style={{ width: '100%' }}
        format="YYYY-MM-DD"
        value={filters?.dateTo ? dayjs(filters.dateTo) : null}
        onChange={(_, to) => onDateValueChange({ dateTo: to as string })}
        placeholder={finishDatePlaceholder || 'Finish Date'}
        allowClear={allowClear}
        disabledDate={endDateDisabled}
      />
    </div>
  );
};

export default DateRangeComponent;
